import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { GridFilterInputValueProps, GridFilterOperator } from '@mui/x-data-grid-pro';

// These are Custom Date Filters. These enable before and after date filter along with in between.
const validDateRegex = /^(200[1-9]|20[1-9][0-9]|2100)-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;

// single date input
function DateInput({ item, applyValue, focusElementRef }: GridFilterInputValueProps) {
    const dateRef = React.useRef<HTMLInputElement>(null);

    React.useImperativeHandle(focusElementRef, () => ({
        focus: () => {
            dateRef.current?.focus();
        }
    }));

    const [inputValue, setInputValue] = React.useState(item.value || '');

    const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(event.target.value);
    };

    useEffect(() => {
        if (validDateRegex.test(inputValue)) {
            applyValue({ ...item, value: inputValue });
        }
    }, [inputValue]);

    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', height: 48, px: 1 }}>
            <TextField
                inputRef={dateRef}
                label='Date'
                type='date'
                variant='standard'
                value={inputValue}
                onChange={handleDateChange}
                InputLabelProps={{ shrink: true }}
            />
        </Box>
    );
}

// Date Intervals
function DateIntervalInput({ item, applyValue, focusElementRef }: GridFilterInputValueProps) {
    const startRef = React.useRef<HTMLInputElement>(null);

    React.useImperativeHandle(focusElementRef, () => ({
        focus: () => {
            startRef.current?.focus();
        }
    }));

    const initialFrom = typeof item.value === 'object' && item.value?.from ? item.value.from : '';
    const initialTo = typeof item.value === 'object' && item.value?.to ? item.value.to : '';

    const [fromValue, setFromValue] = useState<string>(initialFrom);
    const [toValue, setToValue] = useState<string>(initialTo);

    const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFromValue(event.target.value);
    };
    const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setToValue(event.target.value);
    };

    useEffect(() => {
        if (validDateRegex.test(fromValue) && validDateRegex.test(toValue)) {
            applyValue({
                ...item,
                value: { from: fromValue, to: toValue }
            });
        }
    }, [fromValue, toValue]);

    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', height: 48, px: 1, gap: 1.5 }}>
            <TextField
                inputRef={startRef}
                label='From'
                type='date'
                variant='standard'
                value={fromValue}
                onChange={handleStartDateChange}
                InputLabelProps={{ shrink: true }}
            />
            <TextField
                label='To'
                type='date'
                variant='standard'
                value={toValue}
                onChange={handleEndDateChange}
                InputLabelProps={{ shrink: true }}
            />
        </Box>
    );
}

// Filter Operator Definitions
export const customDateFilterOperators: GridFilterOperator[] = [
    {
        label: 'Is Between',
        value: 'between',
        getApplyFilterFn: () => null, // Required by MUI's types
        InputComponent: DateIntervalInput
    },
    {
        label: 'Is After',
        value: 'after',
        getApplyFilterFn: () => null, // Required for type safety.
        InputComponent: DateInput
    },
    {
        label: 'Is Before',
        value: 'before',
        getApplyFilterFn: () => null, // Required for type safety.
        InputComponent: DateInput
    }
];
